import { useCallback } from "react";
import { useFetcher, useMutator } from "~/hooks/useFetcher";
import {
  userPreferencesPath,
  type UserPreferencesResponse,
  type UserPreferencesUpdate,
} from "~/services/preferences";

export interface Personalization {
  /** The stored instructions, or `null` until loaded — the editor holds rather
   *  than mounting on a value it would replace under the user's cursor. */
  text: string | null;
  isSaving: boolean;
  /** Blank text clears the instructions. Rejects reach the caller. */
  save: (next: string) => Promise<void>;
}

/**
 * Reads and writes the user's personalization text.
 *
 * The save is not optimistic: this is text the user typed and expects to see
 * persisted, so the field settles on what the service stored.
 */
export function usePersonalization(): Personalization {
  const key = userPreferencesPath();
  const { data } = useFetcher<UserPreferencesResponse>(key);
  const { trigger, isMutating } = useMutator<UserPreferencesResponse, UserPreferencesUpdate>(key, {
    requestInit: { method: "PUT" },
  });

  const save = useCallback(
    async (next: string) => {
      await trigger({ personalization: next });
    },
    [trigger]
  );

  // A row with no text is an empty field, not an unloaded one.
  return { text: data ? (data.personalization ?? "") : null, isSaving: isMutating, save };
}
